import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Btn,
  Cta,
  Footer,
  ItemRow,
  Row,
  SheetActions,
  Strip,
  V1Screen,
  V1Sheet,
  WaitCta,
} from '@/features/gate/v1/kit'
import { V1Photo } from '@/features/gate/v1/photo'
import { RefusedCard } from '@/features/store/v1/bits'
import { C } from '@/features/gate/v1/style'
import { fmtTime, kg, shortCode, tr } from '@/features/gate/v1/text'
import type {
  CancelRunPayload,
  CloseRunPayload,
  ConfirmCleanoutPayload,
  RecordOutputPayload,
  ReportMixingPayload,
  VoidOutputPayload,
  WeighInPayload,
} from '../commands'
import '../commands'
import {
  isRunLive,
  liveOutputs,
  outputStream,
  runParentIds,
  type BagView,
  type ProcessState,
  type RunView,
} from '../projection'
import {
  canCancel,
  checkWeighInBag,
  runFigures,
  sampleCandidates,
  tieFor,
  type WeighInMatch,
} from '../model/rules'
import { useDraftState } from '../useProcess'
import { k, PIcon, ProcessSprite } from './icons'
import { closeGapsV1, closeResult, freshCodeV1, onBenchScale, outputsDone, sacksOf } from './model'
import {
  Hint,
  LeadBox,
  NextArrow,
  OkPill,
  outputLabel,
  PBand,
  PHeader,
  PictureStep,
  runFarmer,
  SackChip,
  SaveError,
  saveFailed,
  SavedLine,
  stepLabel,
  SumCard,
  TagChip,
  tail2,
  WeightBox,
} from './ui'
import { PScanner, refusalText, ScalePad, useScaleEntry, WithSite } from './parts'

type Stage = 'in' | 'allin' | 'running' | 'out'

interface RunDraftV1 {
  stage: Stage
  /** Outputs confirmed as "nothing came out". */
  zero: string[]
}

/** V1-F6-02 … 05 — one run, from the empty machine to the close. */
export function V1Run() {
  const { runId = '' } = useParams()
  const navigate = useNavigate()
  return (
    <WithSite>
      {(state) => {
        const run = state.runs.get(runId)
        if (!run)
          return (
            <V1Screen
              header={
                <PHeader
                  onBack={() => navigate('/m/process')}
                  icon="machine"
                  title={tr('process.run.missing')}
                />
              }
            >
              <Strip icon="bang">{tr('process.run.missingBody')}</Strip>
            </V1Screen>
          )
        return <Run key={run.id} state={state} run={run} />
      }}
    </WithSite>
  )
}

function Run({ state, run }: { state: ProcessState; run: RunView }) {
  const actor = useActor()
  const [draft, update, loaded] = useDraftState<RunDraftV1>(
    `process.v1.run:${run.id}`,
    'process.v1.run',
    () => ({ stage: 'in', zero: [] }),
    actor.user_id,
  )
  if (!loaded) return <V1Screen />
  const go = (stage: Stage) => update((d) => ({ ...d, stage }))
  if (!isRunLive(run.status) || run.closed_local) return <Closed state={state} run={run} />
  if (!run.cleanout_confirmed_at) return <Cleanout state={state} run={run} />
  if (draft.stage === 'out' && run.inputs.length > 0)
    return (
      <WeighOut
        state={state}
        run={run}
        zero={draft.zero}
        setZero={(zero) => update((d) => ({ ...d, zero }))}
        onBack={() => go('running')}
      />
    )
  if (draft.stage === 'running' && run.inputs.length > 0)
    return <Running state={state} run={run} onBack={() => go('allin')} onWeigh={() => go('out')} />
  if (draft.stage === 'allin' && run.inputs.length > 0)
    return <AllIn state={state} run={run} onBack={() => go('in')} onNext={() => go('running')} />
  return <BagsIn state={state} run={run} onAllIn={() => go('allin')} />
}

/** The header farmer and the run's grade band, shared by the run boards. */
function useRunFrame(state: ProcessState, run: RunView) {
  const navigate = useNavigate()
  const fig = runFigures(run, state.steps.get(run.step_code))
  const grade = tr(run.stream === 'MAIN' ? 'process.v1.check.good' : 'process.v1.check.red')
  return {
    farmer: runFarmer(run),
    home: () => navigate('/m/process'),
    fig,
    band: (children: string) => (
      <PBand
        tone={run.stream === 'MAIN' ? 'good' : 'red'}
        icon={run.stream === 'MAIN' ? 'sack' : 'redtie'}
      >
        {children}
      </PBand>
    ),
    grade,
  }
}

/**
 * रन रद्द करें (in ⋯) while no bag is in yet: the cleanout and the bags-in boards. Returns the ⋯
 * handler (undefined when the run can no longer be cancelled), the sheet and a save error.
 */
function useCancelRun(state: ProcessState, run: RunView) {
  const actor = useActor()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const cancel = async () => {
    setSaving(true)
    setErr(null)
    try {
      await enqueueCommand({
        type: 'cancel_run',
        payload: { run_id: run.id, reason: null } satisfies CancelRunPayload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      navigate('/m/process', { replace: true })
    } catch (e) {
      setErr(saveFailed(e))
      setOpen(false)
    } finally {
      setSaving(false)
    }
  }

  const overlay = open ? (
    <V1Sheet
      title={tr('process.v1.cancel.title')}
      onClose={() => setOpen(false)}
      top={360}
      actions={
        <SheetActions>
          <Btn icon="back" onClick={() => setOpen(false)}>
            {tr('gate.v1.back')}
          </Btn>
          <Cta
            label={tr('process.v1.cancel.yes')}
            icon="x"
            busy={saving}
            onClick={() => void cancel()}
          />
        </SheetActions>
      }
    >
      <span style={{ fontSize: 17 }}>
        {stepLabel(state, run.step_code)}, {state.machines.get(run.equipment_id)?.code} ·{' '}
        {tr('process.v1.cancel.line', { count: run.inputs.length, code: tail2(run.code) })}
      </span>
      <Hint icon="undo">{tr('process.v1.cancel.back')}</Hint>
    </V1Sheet>
  ) : undefined

  return { onMore: canCancel(run) ? () => setOpen(true) : undefined, overlay, err }
}

// ---------------------------------------------------------------------------------------------
// F6.2 — मशीन खाली (cancel in ⋯)
// ---------------------------------------------------------------------------------------------

function Cleanout({ state, run }: { state: ProcessState; run: RunView }) {
  const actor = useActor()
  const f = useRunFrame(state, run)
  const [photo, setPhoto] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const cancel = useCancelRun(state, run)
  const step = state.steps.get(run.step_code)
  const point = step?.photo_points[0]

  const confirm = async () => {
    if (!photo) return
    setSaving(true)
    setErr(null)
    try {
      // V1: one photo of the empty machine; on a crop change it also shows the full clean.
      const payload: ConfirmCleanoutPayload = {
        run_id: run.id,
        photo_ids: [photo],
        photo_points: [point?.key ?? 'HOPPER'],
        allergen_clean_required: run.allergen_clean_required,
        allergen_clean: run.allergen_clean_required,
        allergen_photo_id: run.allergen_clean_required ? photo : null,
      }
      await enqueueCommand({
        type: 'confirm_cleanout',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
        mediaIds: [photo],
      })
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <V1Screen
      header={
        <PHeader
          onBack={f.home}
          farmer={f.farmer}
          icon="broom"
          title={tr('process.v1.clean.title')}
          step={2}
          onMore={cancel.onMore}
        />
      }
      footer={
        <Footer>
          <SaveError text={err ?? cancel.err} />
          {photo ? (
            <Cta
              label={tr('process.v1.clean.done')}
              icon="ok"
              busy={saving}
              onClick={() => void confirm()}
            />
          ) : (
            <WaitCta>{tr('process.v1.clean.wait')}</WaitCta>
          )}
        </Footer>
      }
      overlay={cancel.overlay}
    >
      <SavedLine>{tr('process.v1.clean.opened', { code: tail2(run.code) })}</SavedLine>
      {run.allergen_clean_required && (
        <>
          <Hint icon="broom" tone="bad">
            <b>{tr('process.v1.clean.full')}</b>
          </Hint>
          <PictureStep n={1} icon="broom" text={tr('process.v1.clean.s1')} />
          <PictureStep n={2} icon="hopper" text={tr('process.v1.clean.s2')} />
          <PictureStep n={3} icon="machine" text={tr('process.v1.clean.s3')} />
        </>
      )}
      <V1Photo
        value={photo}
        onChange={setPhoto}
        render={({ preview, busy, take }) => (
          <ItemRow
            padding="8px 10px"
            lead={
              preview ? (
                <img
                  src={preview}
                  alt=""
                  style={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover' }}
                />
              ) : (
                <LeadBox icon="camera" size={52} />
              )
            }
            title={tr('process.v1.clean.photo')}
            line={photo ? <OkPill>{tr('process.v1.clean.taken')}</OkPill> : undefined}
            right={
              <div style={{ flex: '0 0 auto', display: 'flex' }}>
                <Btn icon="camera" disabled={busy} onClick={take}>
                  {tr(photo ? 'process.v1.clean.again' : 'process.v1.clean.take')}
                </Btn>
              </div>
            }
          />
        )}
      />
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F6.3 — बोरी अंदर: scan at the hopper, tip, cut the tag; refusals
// ---------------------------------------------------------------------------------------------

function BagsIn({
  state,
  run,
  onAllIn,
}: {
  state: ProcessState
  run: RunView
  onAllIn: () => void
}) {
  const actor = useActor()
  const f = useRunFrame(state, run)
  // A wrong machine or lot found before the first bag goes in: the run can still be cancelled.
  const cancel = useCancelRun(state, run)
  const [pending, setPending] = useState<{
    match: WeighInMatch
    code: string
    entry: 'SCAN' | 'TYPED'
  } | null>(null)
  const [refused, setRefused] = useState<{
    code: string
    reason: string
    bag: BagView | null
  } | null>(null)
  const [saved, setSaved] = useState<{ n: number; g: number } | null>(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const count = run.inputs.length

  const onCode = (code: string, entry: 'SCAN' | 'TYPED') => {
    const v = checkWeighInBag(state, run, code)
    if (v.ok) {
      setPending({ match: v.value, code, entry })
      return null
    }
    if (v.reason === 'DUPLICATE') return refusalText('DUPLICATE')
    setRefused({ code, reason: v.reason, bag: state.bagByPayload.get(code) ?? null })
    return null
  }

  const tipIn = async () => {
    if (!pending) return
    setSaving(true)
    setErr(null)
    try {
      const id = newId()
      // V1: no scale at the hopper — the bag's last weight is the input weight (the server's).
      const payload: WeighInPayload = {
        run_input_id: id,
        run_id: run.id,
        qr_payload: pending.code,
        entry: pending.entry,
        container_id: pending.match.bag.container_id,
        weight_read_g: null,
        issue_seen: pending.match.issueSeen,
        confirmed_twice: false,
      }
      await enqueueCommand({
        id,
        type: 'weigh_in',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setSaved({ n: count + 1, g: lastWeight(pending.match.bag) })
      setPending(null)
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  if (refused) {
    const bagUnit = refused.bag ? state.units.get(refused.bag.unit_id) : undefined
    return (
      <V1Screen
        header={
          <PHeader
            onBack={() => setRefused(null)}
            farmer={f.farmer}
            icon="hopper"
            title={tr('process.v1.wrong.title')}
            step={3}
          />
        }
        footer={
          <Footer>
            <Cta label={tr('process.v1.wrong.ok')} icon="ok" onClick={() => setRefused(null)} />
          </Footer>
        }
      >
        <RefusedCard
          title={tr('process.v1.wrong.tag', {
            tag: shortCode(refused.code),
            why: refusalText(refused.reason),
          })}
          why=""
          steps={[tr('process.v1.wrong.s1'), tr('process.v1.wrong.s2')]}
        />
        {bagUnit?.farmer_code && (
          <SumCard
            rows={[
              { label: tr('process.v1.wrong.bagOf'), value: `#${bagUnit.farmer_code}` },
              {
                label: tr('process.v1.wrong.runOf'),
                value: run.farmer_code ? `#${run.farmer_code}` : '—',
              },
            ]}
          />
        )}
      </V1Screen>
    )
  }

  if (pending) {
    const lot = state.units.get(pending.match.bag.unit_id)
    return (
      <V1Screen
        header={
          <PHeader
            onBack={() => setPending(null)}
            farmer={f.farmer}
            icon="hopper"
            title={tr('process.v1.in.title')}
            step={3}
          />
        }
        band={f.band(tr('process.v1.in.band', { grade: f.grade, count }))}
        footer={
          <Footer>
            <SaveError text={err} />
            <Cta
              label={tr('process.v1.in.tipped')}
              icon="ok"
              busy={saving}
              onClick={() => void tipIn()}
            />
          </Footer>
        }
      >
        <ItemRow
          padding="8px 10px"
          lead={<LeadBox icon="tag" />}
          title={`${tr('process.v1.tag', { no: shortCode(pending.code) })} · ${tr('process.v1.check.lot', { code: shortCode(lot?.code ?? '') })}`}
          line={<OkPill>{tr('process.v1.in.thisRun')}</OkPill>}
        />
        <WeightBox
          label={tr('process.v1.in.lastWeight')}
          value={kg(lastWeight(pending.match.bag))}
        />
        <PictureStep n={1} icon="hopper" text={tr('process.v1.in.s1')} />
        <PictureStep n={2} icon="scissors" text={tr('process.v1.in.s2')} />
      </V1Screen>
    )
  }

  return (
    <V1Screen
      header={
        <PHeader
          onBack={f.home}
          farmer={f.farmer}
          icon="hopper"
          title={tr(count ? 'process.v1.in.next' : 'process.v1.in.title')}
          step={3}
          onMore={cancel.onMore}
        />
      }
      overlay={cancel.overlay}
      band={f.band(
        count
          ? tr('process.v1.in.bandKg', { grade: f.grade, count, kg: kg(f.fig.input_g) })
          : tr('process.v1.in.band', { grade: f.grade, count }),
      )}
      footer={
        <Footer>
          {count > 0 ? (
            <Cta label={tr('process.v1.in.allIn')} icon="next" onClick={onAllIn} />
          ) : (
            <WaitCta>{tr('process.v1.in.wait')}</WaitCta>
          )}
        </Footer>
      }
    >
      {saved && <SavedLine>{tr('process.v1.in.saved', { n: saved.n, kg: kg(saved.g) })}</SavedLine>}
      <PScanner
        state={state}
        kind="BAG"
        hint={tr(count ? 'process.v1.in.before' : 'process.v1.in.first')}
        onCode={onCode}
      />
    </V1Screen>
  )
}

/** The bag's latest net weight (the gate weight unless weighed again). */
const lastWeight = (b: BagView) => b.issue_net_g ?? b.net_g_current ?? b.net_g_gate ?? 0

/** F6.3-d — सब अंदर. */
function AllIn({
  state,
  run,
  onBack,
  onNext,
}: {
  state: ProcessState
  run: RunView
  onBack: () => void
  onNext: () => void
}) {
  const f = useRunFrame(state, run)
  const count = run.inputs.length
  return (
    <V1Screen
      header={
        <PHeader
          onBack={onBack}
          farmer={f.farmer}
          icon="hopper"
          title={tr('process.v1.in.allTitle')}
          step={3}
        />
      }
      band={f.band(tr('process.v1.in.bandKg', { grade: f.grade, count, kg: kg(f.fig.input_g) }))}
      footer={
        <Footer>
          <Cta label={tr('process.v1.next')} icon="next" onClick={onNext} />
        </Footer>
      }
    >
      <OkPill size={18}>{tr('process.v1.in.count', { count })}</OkPill>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {run.inputs.map((i, n) => (
          <span
            key={i.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              padding: '2px 6px',
              borderRadius: 8,
              background: C.okWash,
              color: C.ok,
              fontWeight: 700,
            }}
          >
            <PIcon name="sack" size={18} />
            {n + 1}
          </span>
        ))}
      </div>
      <Hint icon="scissors">{tr('process.v1.in.tin', { count })}</Hint>
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F6.4 — मशीन चालू; दूसरा माल मिला?
// ---------------------------------------------------------------------------------------------

function Running({
  state,
  run,
  onBack,
  onWeigh,
}: {
  state: ProcessState
  run: RunView
  onBack: () => void
  onWeigh: () => void
}) {
  const actor = useActor()
  const f = useRunFrame(state, run)
  const [mixing, setMixing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const started = run.started_at ?? run.cleanout_confirmed_at ?? run.opened_at

  const report = async () => {
    setSaving(true)
    setErr(null)
    try {
      const payload: ReportMixingPayload = { run_id: run.id, reason_code: 'OTHER', note: null }
      await enqueueCommand({
        type: 'report_mixing',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setMixing(false)
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <V1Screen
      header={
        <PHeader
          onBack={onBack}
          farmer={f.farmer}
          icon="machine"
          title={tr('process.v1.run.title')}
          step={4}
        />
      }
      footer={
        <Footer>
          <SaveError text={err} />
          <Cta label={tr('process.v1.run.weigh')} icon="scale" onClick={onWeigh} />
        </Footer>
      }
      overlay={
        mixing ? (
          <V1Sheet
            title={tr('process.v1.mix.title')}
            onClose={() => setMixing(false)}
            top={400}
            actions={
              <SheetActions>
                <Btn icon="back" onClick={() => setMixing(false)}>
                  {tr('gate.v1.back')}
                </Btn>
                <Cta
                  label={tr('process.v1.mix.yes')}
                  icon={k('mixing')}
                  busy={saving}
                  onClick={() => void report()}
                />
              </SheetActions>
            }
          >
            <Hint icon="redtie" tone="bad">
              {tr('process.v1.mix.body').replaceAll('**', '')}
            </Hint>
          </V1Sheet>
        ) : undefined
      }
    >
      {run.mixing_reason ? (
        <Strip icon="redtie">{tr('process.v1.mix.reported')}</Strip>
      ) : (
        <ItemRow
          padding="8px 10px"
          onClick={() => setMixing(true)}
          lead={<LeadBox icon="mixing" />}
          title={tr('process.v1.run.mixing')}
          right={<NextArrow />}
        />
      )}
      <SumCard
        rows={[
          ...(started
            ? [{ label: tr('process.v1.run.started', { time: fmtTime(started) }), value: '' }]
            : []),
          {
            label: tr('process.v1.in.count', { count: run.inputs.length }),
            value: `${kg(f.fig.input_g)} ${tr('process.v1.kgUnit')}`,
            bold: true,
          },
        ]}
      />
      <PictureStep n={1} icon="sack" text={tr('process.v1.run.tip1')} />
      <PictureStep n={2} icon="broom" text={tr('process.v1.run.tip2')} />
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F6.5 — कौन सा माल? टैग, वज़न, कुछ नहीं, सब तौला, बंद
// ---------------------------------------------------------------------------------------------

type OutView =
  | { kind: 'pick' }
  | { kind: 'tag'; key: string }
  | { kind: 'weigh'; key: string; code: string; entry: 'SCAN' | 'TYPED' }
  | { kind: 'totals' }

function WeighOut({
  state,
  run,
  zero,
  setZero,
  onBack,
}: {
  state: ProcessState
  run: RunView
  zero: string[]
  setZero: (z: string[]) => void
  onBack: () => void
}) {
  const actor = useActor()
  const f = useRunFrame(state, run)
  const step = state.steps.get(run.step_code)
  const outputs = step?.outputs ?? []
  const [view, setView] = useState<OutView>(() =>
    outputsDone(run, step, zero) ? { kind: 'totals' } : { kind: 'pick' },
  )
  const [noneSheet, setNoneSheet] = useState(false)
  const [fixSheet, setFixSheet] = useState(false)
  const [closeSheet, setCloseSheet] = useState(false)
  const [savedLine, setSavedLine] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const sack =
    state.pack.sack_types.find((s) => s.default_output) ?? state.pack.sack_types[0] ?? null
  const live = liveOutputs(run)
  const name = (key: string) => outputLabel(state, run.step_code, key)
  const isRed = (key: string) => tieFor(outputStream(step, key, run.stream)) === 'CONV'
  const scale = useScaleEntry(state, sack?.tare_g ?? 0)
  const usedHere = new Set(live.map((o) => o.qr_payload.toUpperCase()))

  const save = async (key: string, code: string, entry: 'SCAN' | 'TYPED') => {
    if (!sack) return
    const reading = scale.submit()
    if (!reading) return
    setSaving(true)
    setErr(null)
    try {
      const container_id = newId()
      const def = outputs.find((o) => o.key === key)
      const same = live.find((o) => o.stream_key === key)
      const payload: RecordOutputPayload = {
        container_id,
        unit_id: same?.unit_id ?? newId(),
        run_id: run.id,
        qr_payload: code,
        entry,
        stream_key: key,
        grade: def?.grade ?? null,
        sack_type_id: sack.id,
        tare_g: sack.tare_g,
        weight_read_g: reading.read_g,
        confirmed_twice: reading.confirmed_twice,
        moisture_pct: null,
        tie: isRed(key) ? 'CONV' : 'NONE',
      }
      await enqueueCommand({
        id: container_id,
        type: 'record_output',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      if (zero.includes(key)) setZero(zero.filter((z) => z !== key))
      setSavedLine(
        tr('process.v1.tag.saved', {
          n: sacksOf(run, key).length + 1,
          kg: kg(reading.read_g - sack.tare_g),
        }),
      )
      scale.reset()
      // A waste sack is one sack; a product goes on to the next sack.
      const out = outputs.find((o) => o.key === key)
      setView(out && onBenchScale(out) ? { kind: 'pick' } : { kind: 'tag', key })
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  const voidLast = async (key: string) => {
    const last = sacksOf(run, key).at(-1)
    if (!last) return
    setSaving(true)
    try {
      const payload: VoidOutputPayload = {
        run_id: run.id,
        container_id: last.container_id,
        reason: 'WRONG_WEIGHT',
      }
      await enqueueCommand({
        type: 'void_output',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setFixSheet(false)
      setSavedLine(null)
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  const close = async () => {
    setSaving(true)
    setErr(null)
    try {
      const fig = runFigures(run, step)
      const payload: CloseRunPayload = {
        run_id: run.id,
        ended_at: new Date().toISOString(),
        zero_streams: zero,
        params: {},
        phone_totals: {
          input_g: fig.input_g,
          output_g: fig.flush_g + fig.product_g + fig.byproduct_g + fig.reject_g,
          bags_in: run.inputs.length,
          sacks_out: live.length,
        },
      }
      await enqueueCommand({
        type: 'close_run',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setCloseSheet(false)
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  // F6.5-b — टैग लगाएँ
  if (view.kind === 'tag') {
    const key = view.key
    const n = sacksOf(run, key).length + 1
    const onCode = (code: string, entry: 'SCAN' | 'TYPED') => {
      const v = freshCodeV1(state, code, 'OUTPUT_BAG', actor.user_id, usedHere)
      if (!v.ok) return refusalText(v.reason)
      setView({ kind: 'weigh', key, code: v.value, entry })
      return null
    }
    return (
      <V1Screen
        header={
          <PHeader
            onBack={() => setView({ kind: 'pick' })}
            farmer={f.farmer}
            icon="tag"
            title={tr('process.v1.tag.title')}
            step={5}
          />
        }
        band={
          <PBand tone={isRed(key) ? 'red' : 'good'} icon={isRed(key) ? 'redtie' : 'sack'}>
            {tr('process.v1.tag.band', {
              grade: isRed(key) ? tr('process.v1.check.red') : f.grade,
              name: name(key),
              n,
            })}
          </PBand>
        }
        footer={
          <Footer>
            <SaveError text={err} />
            {sacksOf(run, key).length > 0 ? (
              <Cta
                label={tr('process.v1.tag.done', { name: name(key) })}
                icon="ok"
                onClick={() => (setSavedLine(null), setView({ kind: 'pick' }))}
              />
            ) : (
              <WaitCta>{tr('process.v1.tag.hint')}</WaitCta>
            )}
          </Footer>
        }
        overlay={
          fixSheet ? (
            <V1Sheet
              title={tr('process.v1.fix.title')}
              onClose={() => setFixSheet(false)}
              top={380}
              actions={
                <SheetActions>
                  <Btn icon="back" onClick={() => setFixSheet(false)}>
                    {tr('gate.v1.back')}
                  </Btn>
                  <Cta
                    label={tr('process.v1.fix.yes')}
                    icon="undo"
                    busy={saving}
                    onClick={() => void voidLast(key)}
                  />
                </SheetActions>
              }
            >
              <span style={{ fontSize: 18 }}>{tr('process.v1.fix.body')}</span>
            </V1Sheet>
          ) : undefined
        }
      >
        {savedLine && <SavedLine>{savedLine}</SavedLine>}
        <PScanner
          state={state}
          kind="OUTPUT_BAG"
          hint={tr('process.v1.tag.hint')}
          onCode={onCode}
          paused={fixSheet}
          extra={
            sacksOf(run, key).length > 0 ? (
              <Btn icon="pencil" onClick={() => setFixSheet(true)}>
                {tr('process.v1.tag.fixLast')}
              </Btn>
            ) : undefined
          }
        />
      </V1Screen>
    )
  }

  // F6.5-c/d — वज़न लिखें (platform scale) / छोटा काँटा (waste on the bench scale)
  if (view.kind === 'weigh') {
    const out = outputs.find((o) => o.key === view.key)
    const bench = !!out && onBenchScale(out)
    const n = sacksOf(run, view.key).length + 1
    return (
      <V1Screen
        main={{ padding: '6px 12px', gap: 6 }}
        header={
          <PHeader
            onBack={() => (scale.reset(), setView({ kind: 'tag', key: view.key }))}
            farmer={f.farmer}
            icon={bench ? 'smallscale' : 'scale'}
            title={tr(bench ? 'process.v1.weigh.bench' : 'process.v1.weigh.title')}
            step={5}
          />
        }
        band={
          <PBand tone={isRed(view.key) ? 'red' : 'good'} icon={isRed(view.key) ? 'redtie' : 'sack'}>
            {bench
              ? `${tr('process.v1.check.red')} · ${name(view.key)}`
              : tr('process.v1.tag.band', {
                  grade: isRed(view.key) ? tr('process.v1.check.red') : f.grade,
                  name: name(view.key),
                  n,
                })}
          </PBand>
        }
        footer={
          <Footer dense>
            <SaveError text={err} />
            {scale.value ? (
              <Cta
                label={tr(bench ? 'process.v1.weigh.save' : 'process.v1.weigh.saveNext')}
                icon="ok"
                busy={saving}
                onClick={() => void save(view.key, view.code, view.entry)}
              />
            ) : (
              <WaitCta>{tr('process.v1.weigh.wait')}</WaitCta>
            )}
          </Footer>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>
          <TagChip
            code={view.code}
            onBad={() => (scale.reset(), setView({ kind: 'tag', key: view.key }))}
          />
          {sack && <SackChip name={sack.name} tare={kg(sack.tare_g)} />}
        </div>
        <ScalePad
          label={bench ? tr('process.v1.weigh.labelWaste') : tr('process.v1.weigh.label', { n })}
          entry={scale}
        />
      </V1Screen>
    )
  }

  // F6.5-e — सब माल तौला?
  if (view.kind === 'totals') {
    const gaps = closeGapsV1(run, step, zero).filter((g) => g.kind === 'OUTPUT_MISSING')
    return (
      <V1Screen
        header={
          <PHeader
            onBack={() => setView({ kind: 'pick' })}
            farmer={f.farmer}
            icon="stamp"
            title={tr('process.v1.totals.title')}
            step={5}
          />
        }
        footer={
          <Footer>
            <SaveError text={err} />
            {gaps.length === 0 ? (
              <Cta
                label={tr('process.v1.totals.close')}
                icon={k('stamp')}
                onClick={() => setCloseSheet(true)}
              />
            ) : (
              <WaitCta onClick={() => setView({ kind: 'pick' })}>
                {tr('process.v1.totals.missing', {
                  name: name(gaps[0]!.kind === 'OUTPUT_MISSING' ? gaps[0]!.key : ''),
                })}
              </WaitCta>
            )}
          </Footer>
        }
        overlay={
          closeSheet ? (
            <V1Sheet
              title={tr('process.v1.close.title')}
              onClose={() => setCloseSheet(false)}
              top={380}
              actions={
                <SheetActions>
                  <Btn icon="back" onClick={() => setCloseSheet(false)}>
                    {tr('gate.v1.back')}
                  </Btn>
                  <Cta
                    label={tr('process.v1.close.yes')}
                    icon={k('stamp')}
                    busy={saving}
                    onClick={() => void close()}
                  />
                </SheetActions>
              }
            >
              <span style={{ fontSize: 17 }}>
                {stepLabel(state, run.step_code)}, {state.machines.get(run.equipment_id)?.code} ·{' '}
                {tr('process.v1.close.line', { bags: run.inputs.length, sacks: live.length })}
              </span>
              <Hint icon="bang" tone="bad">
                {tr('process.v1.close.locked')}
              </Hint>
            </V1Sheet>
          ) : undefined
        }
      >
        {savedLine && <SavedLine>{savedLine}</SavedLine>}
        <SumCard
          rows={[
            {
              label: tr('process.v1.totals.in'),
              value: tr('process.v1.out.sacks', { count: run.inputs.length }),
              bold: true,
            },
            'rule',
            ...outputs.map((o) => ({
              label: name(o.key),
              value: zero.includes(o.key)
                ? tr('process.v1.out.none')
                : String(sacksOf(run, o.key).length),
            })),
            'rule',
            {
              label: tr('process.v1.totals.out'),
              value: tr('process.v1.out.sacks', { count: live.length }),
              bold: true,
            },
          ]}
        />
        <Row>
          <Btn icon={k('plus')} onClick={() => setView({ kind: 'pick' })}>
            {tr('process.v1.totals.more')}
          </Btn>
        </Row>
      </V1Screen>
    )
  }

  // F6.5-a — कौन सा माल?
  const done = outputsDone(run, step, zero)
  return (
    <V1Screen
      header={
        <PHeader
          onBack={onBack}
          farmer={f.farmer}
          icon="scale"
          title={tr('process.v1.out.title')}
          step={5}
        />
      }
      footer={
        <Footer>
          <SaveError text={err} />
          {done ? (
            <Cta
              label={tr('process.v1.next')}
              icon="next"
              onClick={() => setView({ kind: 'totals' })}
            />
          ) : (
            <WaitCta>{tr('process.v1.out.wait')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        noneSheet ? (
          <V1Sheet
            title={tr('process.v1.none.title')}
            onClose={() => setNoneSheet(false)}
            top={240}
            actions={
              <Btn icon="back" onClick={() => setNoneSheet(false)}>
                {tr('gate.v1.back')}
              </Btn>
            }
          >
            {outputs
              .filter((o) => sacksOf(run, o.key).length === 0)
              .map((o) => (
                <ItemRow
                  key={o.key}
                  padding="10px 12px"
                  onClick={() => {
                    setZero(
                      zero.includes(o.key) ? zero.filter((z) => z !== o.key) : [...zero, o.key],
                    )
                    setNoneSheet(false)
                  }}
                  border={zero.includes(o.key) ? C.ink : C.border}
                  borderWidth={zero.includes(o.key) ? 3 : 1.5}
                  title={name(o.key)}
                />
              ))}
            <Hint icon="scale">{tr('process.v1.none.zero')}</Hint>
          </V1Sheet>
        ) : undefined
      }
    >
      {savedLine && <SavedLine>{savedLine}</SavedLine>}
      {outputs.map((o) => {
        const sacks = sacksOf(run, o.key).length
        const red = isRed(o.key)
        return (
          <ItemRow
            key={o.key}
            padding="8px 10px"
            onClick={() => (setSavedLine(null), setView({ kind: 'tag', key: o.key }))}
            lead={<LeadBox icon={red ? 'redtie' : 'sack'} color={red ? C.bad : C.ok} />}
            title={name(o.key)}
            line={
              zero.includes(o.key)
                ? tr('process.v1.out.none')
                : tr('process.v1.out.sacks', { count: sacks })
            }
            right={<NextArrow />}
          />
        )
      })}
      <Row>
        <Btn onClick={() => setNoneSheet(true)}>
          <span style={{ fontWeight: 800, marginRight: 6 }}>0</span>
          {tr('process.v1.out.none')}
        </Btn>
      </Row>
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F6.5-g — रन बंद (now the kilos are shown)
// ---------------------------------------------------------------------------------------------

function Closed({ state, run }: { state: ProcessState; run: RunView }) {
  const navigate = useNavigate()
  const step = state.steps.get(run.step_code)
  const r = closeResult(run, step)
  const toSample = sampleCandidates(state).some((c) => c.run?.id === run.id)
  const live = liveOutputs(run)
  const note =
    r.kind === 'ZERO'
      ? tr('process.v1.closed.zero')
      : r.kind === 'MORE'
        ? tr('process.v1.closed.more', { kg: kg(-r.residual_g) })
        : r.beyond
          ? tr('process.v1.closed.less', { kg: kg(r.residual_g) })
          : tr('process.v1.closed.lessOk', { kg: kg(r.residual_g) })
  return (
    <V1Screen
      main={{ padding: '0', gap: 0 }}
      footer={
        <Footer>
          {toSample ? (
            <Cta
              label={tr('process.v1.closed.sample')}
              icon={k('scoop')}
              onClick={() => navigate('/m/process/qc-sample')}
            />
          ) : (
            <Cta
              label={tr('process.v1.closed.home')}
              icon="home"
              lead
              onClick={() => navigate('/m/process')}
            />
          )}
        </Footer>
      }
    >
      <div
        role="status"
        style={{
          flexGrow: 1,
          background: C.ok,
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 20,
          textAlign: 'center',
        }}
      >
        <ProcessSprite />
        <span
          style={{
            width: 110,
            height: 110,
            borderRadius: 55,
            background: '#ffffff',
            color: C.ok,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PIcon name="ok" size={70} sw={3.2} />
        </span>
        <h1 style={{ margin: 0, fontSize: 30 }}>{tr('process.v1.closed.title')}</h1>
        <span style={{ fontSize: 20 }}>
          {tr('process.v1.close.line', { bags: run.inputs.length, sacks: live.length })}
        </span>
        <span style={{ fontSize: 18 }}>{tr('process.v1.closed.place')}</span>
        <span
          style={{
            fontSize: 18,
            padding: '8px 12px',
            borderRadius: 12,
            background: 'rgba(0,0,0,.18)',
          }}
        >
          {tr('process.v1.closed.kg', { in: kg(r.input_g), out: kg(r.output_g) })}
          <br />
          <b>{note}</b>
        </span>
      </div>
    </V1Screen>
  )
}
